import { useCallback, useEffect, useRef, useState } from "react";

// FREE voice using browser Web Speech API.
// Speech-to-text (SpeechRecognition) + Text-to-speech (SpeechSynthesis).
// Replaces paid Gemini Live API for users who don't want to pay.

const SpeechRecognitionImpl =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export const speechSupported = !!SpeechRecognitionImpl;
export const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;

// ============================================================
// SPEECH-TO-TEXT
// ============================================================

export function useSpeechRecognition({ lang = "en-US", continuous = false, interimResults = true } = {}) {
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const finalTextRef = useRef("");

  const init = useCallback(() => {
    if (!SpeechRecognitionImpl) {
      setError("Speech recognition not supported in this browser. Try Chrome/Edge.");
      return null;
    }
    const rec = new SpeechRecognitionImpl();
    rec.lang = lang;
    rec.continuous = continuous;
    rec.interimResults = interimResults;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interimText = "";
      let finalText = finalTextRef.current;
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const txt = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += txt;
        } else {
          interimText += txt;
        }
      }
      finalTextRef.current = finalText;
      setTranscript(finalText);
      setInterim(interimText);
    };

    rec.onerror = (e) => {
      if (e.error === "no-speech" || e.error === "aborted") return;
      setError(`Speech error: ${e.error}`);
      setListening(false);
    };

    rec.onend = () => {
      setListening(false);
      setInterim("");
    };

    return rec;
  }, [lang, continuous, interimResults]);

  const start = useCallback(() => {
    if (listening) return;
    setError(null);
    setTranscript("");
    setInterim("");
    finalTextRef.current = "";
    const rec = init();
    if (!rec) return;
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      setError("Could not start microphone. Check browser permissions.");
    }
  }, [init, listening]);

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
    }
    setListening(false);
  }, []);

  const reset = useCallback(() => {
    finalTextRef.current = "";
    setTranscript("");
    setInterim("");
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {}
      }
    };
  }, []);

  return {
    transcript,
    interim,
    listening,
    error,
    supported: speechSupported,
    start,
    stop,
    reset,
  };
}

// ============================================================
// TEXT-TO-SPEECH
// ============================================================

let voicesCache = null;

function loadVoices() {
  if (!ttsSupported) return [];
  if (voicesCache && voicesCache.length) return voicesCache;
  const v = window.speechSynthesis.getVoices();
  if (v.length) voicesCache = v;
  return v;
}

export function useSpeechSynthesis() {
  const [speaking, setSpeaking] = useState(false);
  const [voices, setVoices] = useState(loadVoices());

  useEffect(() => {
    if (!ttsSupported) return;
    const update = () => {
      const v = window.speechSynthesis.getVoices();
      voicesCache = v;
      setVoices(v);
    };
    update();
    window.speechSynthesis.onvoiceschanged = update;
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  const speak = useCallback(
    (text, opts = {}) => {
      if (!ttsSupported || !text) return;
      const { lang = "en-US", rate = 0.95, pitch = 1, volume = 1, voiceName, onEnd } = opts;
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = lang;
      utter.rate = rate;
      utter.pitch = pitch;
      utter.volume = volume;
      const v = window.speechSynthesis.getVoices();
      if (voiceName) {
        const match = v.find((vv) => vv.name === voiceName);
        if (match) utter.voice = match;
      } else {
        // Pick a female English voice if available (more pleasant for learning)
        const preferred =
          v.find((vv) => vv.lang === lang && /female|samantha|karen|moira|tessa|google/i.test(vv.name)) ||
          v.find((vv) => vv.lang === lang) ||
          v.find((vv) => vv.lang.startsWith(lang.split("-")[0]));
        if (preferred) utter.voice = preferred;
      }
      utter.onend = () => {
        setSpeaking(false);
        if (onEnd) onEnd();
      };
      utter.onerror = () => setSpeaking(false);
      setSpeaking(true);
      window.speechSynthesis.speak(utter);
    },
    []
  );

  const cancel = useCallback(() => {
    if (!ttsSupported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, []);

  return { speak, cancel, speaking, voices, supported: ttsSupported };
}

// ============================================================
// PRONUNCIATION SCORING (simple word-match, FREE)
// ============================================================

export function scorePronunciation(target, spoken) {
  if (!target || !spoken) return { score: 0, matched: 0, total: 0 };
  const norm = (s) =>
    s
      .toLowerCase()
      .replace(/[.,!?;:'"()-]/g, "")
      .split(/\s+/)
      .filter(Boolean);
  const t = norm(target);
  const s = norm(spoken);
  let matched = 0;
  const used = new Array(s.length).fill(false);
  t.forEach((word) => {
    const idx = s.findIndex((sw, i) => !used[i] && sw === word);
    if (idx >= 0) {
      matched++;
      used[idx] = true;
    }
  });
  const score = Math.round((matched / Math.max(t.length, 1)) * 100);
  return { score, matched, total: t.length, missingWords: t.filter((w) => !s.includes(w)) };
}
